import styled from "styled-components";
import { useRecoilState } from "recoil";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faVolumeMute,
  faVolumeUp,
  faPause,
  faPlay,
  faFastForward,
  faRandom,
  faStar,
  faExpand,
  faCompress,
  faMusic,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { Button } from "../App.styles";
import { featuredState, playingState, shuffleState, useShuffleSlideshowMutation } from "./Slideshow.state";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Tooltip } from "@material-ui/core";
import { useTranslation } from "react-i18next";

const SlideshowControlWrapper = styled.div`
  position: absolute;
  z-index: 10000;

  button,
  .button-wrapper {
    margin: 0 0.5rem;
  }

  .button-wrapper {
    display: inline-block;

    button {
      margin: 0;
    }
  }

  @media (max-width: ${({ theme }) => theme.maxMobileWidth}) {
    display: flex;
    ${({ top }) => top && `top: 0;`};
    ${({ right }) => right && `right: 0;`};
    ${({ bottom }) => bottom && `bottom: 0;`};
    ${({ left }) => left && `left: 0;`};

    ${({ left }) => left && `width: 66.6%;`};
    ${({ right }) =>
      right &&
      `
      justify-content: flex-end;
      width: 33.3%;
    `};

    button,
    .button-wrapper {
      border-radius: 0;
      box-shadow: none;
      flex-grow: 1;
      height: 3.5rem;
      margin: 0;

      &:active,
      &:focus,
      &:hover {
        box-shadow: none;
      }
    }

    .button-wrapper button {
      width: 100%;
      height: 100%;
      border-radius: 0;
    }
  }

  @media (min-width: ${({ theme }) => theme.minDesktopWidth}) {
    ${({ top }) => top && `top: ${top};`};
    ${({ right }) => right && `right: ${right};`};
    ${({ bottom }) => bottom && `bottom: ${bottom};`};
    ${({ left }) => left && `left: ${left};`};
  }
`;

const IconCompound = styled.span`
  position: relative;

  .custom-muted {
    width: 5px;
    position: absolute;
    top: 50%;
    right: 0;
    transform: translate3d(7px, -50%, 0);
  }
`

const SlideshowControls = (props) => {
  const { muted, onSkip, onMute, onUnmute, featuredSubmissions, bgMuted, onBgMute, onBgUnmute } = props
  const [featured, setFeatured] = useRecoilState(featuredState);
  const [playing, setPlaying] = useRecoilState(playingState);
  const { game_id } = useParams();
  const { mutate: shuffleSlideshow } = useShuffleSlideshowMutation(game_id);
  const [shuffle, setShuffle] = useRecoilState(shuffleState);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { t } = useTranslation();
  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.body.requestFullscreen();
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  } 

  return (
    <>
      <SlideshowControlWrapper bottom="1.5rem" left="1.5rem">
        <Tooltip title={shuffle ? t("Turn off shuffle") : t("Turn on shuffle")}>
          <span className="button-wrapper">
            <Button
              color={shuffle ? "accent1" : "light"}
              onClick={() => {
                shuffleSlideshow(!shuffle);
                setShuffle(!shuffle);
              }}
            >
              <FontAwesomeIcon icon={faRandom} />
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={muted ? t("Unmute") : t("Mute")}>
          <span className="button-wrapper">
            <Button
              color={muted ? "accent1" : "info"}
              onClick={() => (muted ? onUnmute() : onMute())}
            >
              <FontAwesomeIcon icon={muted ? faVolumeMute : faVolumeUp} />
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={bgMuted ? t("Unmute Background") : t("Mute Background")}>
          <span className="button-wrapper">
            <Button
              color={bgMuted ? "accent1" : "info"}
              onClick={() => (bgMuted ? onBgUnmute() : onBgMute())}
            >
              {bgMuted ? (
                <IconCompound>
                  <FontAwesomeIcon icon={faMusic} />
                  <FontAwesomeIcon icon={faTimes} className="custom-muted" />
                </IconCompound>
              ) : (
                <FontAwesomeIcon icon={faMusic} />
              )}
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={
            featuredSubmissions > 0
              ? featured
                ? t("Show all submissions")
                : t("Show featured submissions only")
              : t("No featured submissions available")
          }
        >
          <span className="button-wrapper">
            <Button
              color={featured ? "accent1" : "dark"}
              onClick={() => setFeatured(!featured)}
              disabled={featuredSubmissions === 0}
            >
              <FontAwesomeIcon icon={faStar} />
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={isFullscreen ? t('Exit Fullscreen') : t('Fullscreen')}>
          <span className="button-wrapper">
            <Button
              color="info"
              onClick={toggleFullScreen}
            >
              <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
            </Button>
          </span>
        </Tooltip>
      </SlideshowControlWrapper>
      <SlideshowControlWrapper bottom="1.5rem" right="1.5rem">
        <Tooltip title={playing ? t("Pause") : t("Play")}>
          <span className="button-wrapper">
            <Button
              color={playing ? "accent1" : "success"}
              onClick={() => setPlaying(!playing)}
            >
              <FontAwesomeIcon icon={playing ? faPause : faPlay} />
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={t("Next submission")}>
          <span className="button-wrapper">
            <Button color="info" onClick={onSkip}>
              <FontAwesomeIcon icon={faFastForward} />
            </Button>
          </span>
        </Tooltip>
      </SlideshowControlWrapper>
    </>
  );
};

export default SlideshowControls;
